import { useCallback } from 'react';
import styled, { css } from 'styled-components';

import { ApprovalRequest } from '#/api/terminal/types';

interface Props {
  readonly approval: ApprovalRequest;
  readonly onRespond: (approvalId: string, approved: boolean) => void;
}

export default function ApprovalCard({ approval, onRespond }: Props) {
  const handleApprove = useCallback(() => {
    onRespond(approval.approvalId, true);
  }, [approval.approvalId, onRespond]);

  const handleDeny = useCallback(() => {
    onRespond(approval.approvalId, false);
  }, [approval.approvalId, onRespond]);

  const isPending = approval.state === 'pending';

  return (
    <Card $state={approval.state}>
      <Header>
        <StatusIcon $state={approval.state}>
          {approval.state === 'approved'
            ? '✓'
            : approval.state === 'denied'
            ? '✗'
            : '⚠'}
        </StatusIcon>
        <Title>
          {approval.state === 'approved'
            ? 'Approved'
            : approval.state === 'denied'
            ? 'Denied'
            : 'Approval Required'}
        </Title>
      </Header>
      {approval.description != null && approval.description.length > 0 && (
        <Description>{approval.description}</Description>
      )}
      <CommandBlock>{approval.command}</CommandBlock>
      {isPending ? (
        <ButtonRow>
          <ApproveButton onClick={handleApprove}>Approve</ApproveButton>
          <DenyButton onClick={handleDeny}>Deny</DenyButton>
        </ButtonRow>
      ) : null}
    </Card>
  );
}

const Card = styled.div<{ $state: ApprovalRequest['state'] }>`
  padding: 12px;
  border-radius: 8px;
  border: 1px solid
    ${({ $state }) =>
      $state === 'approved'
        ? 'var(--accent-color, #4caf50)'
        : $state === 'denied'
        ? 'var(--error-color, #f44336)'
        : '#ff9800'};
  background: ${({ $state }) =>
    $state === 'approved'
      ? 'rgba(76, 175, 80, 0.06)'
      : $state === 'denied'
      ? 'rgba(244, 67, 54, 0.06)'
      : 'rgba(255, 152, 0, 0.06)'};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`;

const StatusIcon = styled.span<{ $state: ApprovalRequest['state'] }>`
  font-size: 0.875rem;
  ${({ $state }) =>
    $state === 'approved'
      ? css`
          color: var(--accent-color, #4caf50);
        `
      : $state === 'denied'
      ? css`
          color: var(--error-color, #f44336);
        `
      : css`
          color: #ff9800;
        `}
`;

const Title = styled.span`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Description = styled.div`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  margin-bottom: 6px;
  line-height: 1.4;
`;

const CommandBlock = styled.pre`
  font-family: var(--font-mono, monospace);
  font-size: 0.6875rem;
  line-height: 1.5;
  background: rgba(var(--color-lightest, 255, 255, 255), 0.06);
  border: 1px solid var(--border-color);
  border-radius: 4px;
  padding: 6px 10px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-all;
  color: var(--text-primary);
  margin: 0;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
`;

const baseButton = css`
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
`;

const ApproveButton = styled.button`
  ${baseButton}
  background: var(--accent-color, #4caf50);
  color: var(--cta-text-color, #fff);
`;

const DenyButton = styled.button`
  ${baseButton}
  background: none;
  border: 1px solid var(--border-color);
  color: var(--text-secondary);

  &:hover {
    color: var(--text-primary);
  }
`;
